import { useState } from 'react';
import {
  INTEGRATION_CONNECTOR_LABELS,
  type IntegrationConnectorType,
  type IntegrationPublic,
} from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { BrevoCampaignsPanel } from './BrevoCampaignsPanel';
import { WordPressConnectPanel } from './WordPressConnectPanel';
import {
  connectorShortLabel,
  formatIntegrationError,
  integrationStatusLabel,
  permissionFixHint,
} from './integrationHelp';
import styles from './IntegrationConnectionCard.module.css';

function statusBadgeClass(status: IntegrationPublic['status']): string {
  switch (status) {
    case 'active':
      return styles.badgeActive;
    case 'error':
      return styles.badgeError;
    case 'pending':
      return styles.badgePending;
    default:
      return styles.badgeDisabled;
  }
}

function avatarClass(type: IntegrationConnectorType): string {
  if (type === 'gsc') return `${styles.avatar} ${styles.avatarGsc}`;
  if (type === 'google_ads') return `${styles.avatar} ${styles.avatarAds}`;
  if (type === 'pagespeed') return `${styles.avatar} ${styles.avatarPsi}`;
  if (type === 'brevo') return `${styles.avatar} ${styles.avatarBrevo}`;
  if (type === 'wordpress') return `${styles.avatar} ${styles.avatarWp}`;
  return styles.avatar;
}

type Props = {
  integration: IntegrationPublic;
  expanded: boolean;
  onToggle: () => void;
  onTest: () => void;
  onSync: () => void;
  onDelete: () => void;
  onSaveConfig: (config: Record<string, unknown>) => void;
  busy: boolean;
  projectSlug?: string;
  apiOrigin?: string;
};

export function IntegrationConnectionCard({
  integration,
  expanded,
  onToggle,
  onTest,
  onSync,
  onDelete,
  onSaveConfig,
  busy,
  projectSlug,
  apiOrigin,
}: Props) {
  const [propertyId, setPropertyId] = useState(String(integration.config.property_id ?? ''));
  const [siteUrl, setSiteUrl] = useState(String(integration.config.site_url ?? ''));
  const [senderEmail, setSenderEmail] = useState(
    String(integration.config.default_sender_email ?? ''),
  );
  const [senderName, setSenderName] = useState(String(integration.config.default_sender_name ?? ''));
  const sendViaBrevo = integration.config.send_via_brevo !== false;
  const emitBrevoEvents = integration.config.emit_brevo_events === true;
  const brevoSandbox = integration.config.brevo_sandbox_dry_run === true;
  const inboxViaBrevo = integration.config.inbox_via_brevo === true;

  const fix = permissionFixHint(integration.connector_type, integration.last_error, integration.config);
  const canSync =
    integration.connector_type === 'ga4' ||
    integration.connector_type === 'gsc' ||
    integration.connector_type === 'brevo' ||
    integration.connector_type === 'wordpress';

  const summary = summaryLine(integration);

  return (
    <article
      className={`${styles.card} ${integration.status === 'error' ? styles.cardError : ''} ${expanded ? styles.cardExpanded : ''}`}
    >
      <button type="button" className={styles.summaryRow} onClick={onToggle} aria-expanded={expanded}>
        <div className={avatarClass(integration.connector_type)} aria-hidden>
          {connectorShortLabel(integration.connector_type)}
        </div>
        <div className={styles.summaryText}>
          <span className={styles.cardTitle}>{INTEGRATION_CONNECTOR_LABELS[integration.connector_type]}</span>
          <span className={styles.summaryMeta}>{summary}</span>
        </div>
        <span className={`${styles.badge} ${statusBadgeClass(integration.status)}`}>
          {integrationStatusLabel(integration.status)}
        </span>
        <span className={styles.chevron} aria-hidden>
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded ? (
        <div className={styles.body}>
          <dl className={styles.facts}>
            {factRows(integration).map(([label, value]) => (
              <div key={label} className={styles.fact}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          {integration.status === 'error' || integration.last_error ? (
            <div className={styles.errorBox}>
              <p className={styles.errorTitle}>{fix?.title ?? 'Synchronisatie mislukt'}</p>
              <p className={styles.errorMessage}>
                {integration.last_error
                  ? formatIntegrationError(integration.last_error)
                  : 'Geen fouttekst. Probeer Sync opnieuw na het oplossen van rechten of config.'}
              </p>
              {fix ? (
                <ol className={styles.fixList}>
                  {fix.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              ) : null}
            </div>
          ) : null}

          {integration.connector_type === 'wordpress' && projectSlug && apiOrigin ? (
            <WordPressConnectPanel
              integrationId={integration.id}
              projectSlug={projectSlug}
              apiOrigin={apiOrigin}
              siteUrl={
                typeof integration.config.site_url === 'string'
                  ? integration.config.site_url
                  : null
              }
              busy={busy}
            />
          ) : null}

          {integration.connector_type === 'brevo' ? (
            <details className={styles.details}>
              <summary>Brevo-instellingen &amp; campagnes</summary>
              <div className={styles.detailsBody}>
                <label className={styles.check}>
                  <input
                    type="checkbox"
                    checked={sendViaBrevo}
                    onChange={(e) =>
                      onSaveConfig({
                        send_via_brevo: e.target.checked,
                        emit_brevo_events: emitBrevoEvents,
                        brevo_sandbox_dry_run: brevoSandbox,
                        inbox_via_brevo: inboxViaBrevo,
                        default_sender_email: senderEmail.trim() || null,
                        default_sender_name: senderName.trim() || null,
                      })
                    }
                  />
                  Mail via Brevo API
                </label>
                <label className={styles.check}>
                  <input
                    type="checkbox"
                    checked={emitBrevoEvents}
                    onChange={(e) =>
                      onSaveConfig({
                        send_via_brevo: sendViaBrevo,
                        emit_brevo_events: e.target.checked,
                        brevo_sandbox_dry_run: brevoSandbox,
                        inbox_via_brevo: inboxViaBrevo,
                        default_sender_email: senderEmail.trim() || null,
                        default_sender_name: senderName.trim() || null,
                      })
                    }
                  />
                  Custom events bij statuswijziging
                </label>
                <label className={styles.check}>
                  <input
                    type="checkbox"
                    checked={brevoSandbox}
                    onChange={(e) =>
                      onSaveConfig({
                        send_via_brevo: sendViaBrevo,
                        emit_brevo_events: emitBrevoEvents,
                        brevo_sandbox_dry_run: e.target.checked,
                        inbox_via_brevo: inboxViaBrevo,
                        default_sender_email: senderEmail.trim() || null,
                        default_sender_name: senderName.trim() || null,
                      })
                    }
                  />
                  Sandbox dry-run
                </label>
                <label className={styles.check}>
                  <input
                    type="checkbox"
                    checked={inboxViaBrevo}
                    onChange={(e) =>
                      onSaveConfig({
                        send_via_brevo: sendViaBrevo,
                        emit_brevo_events: emitBrevoEvents,
                        brevo_sandbox_dry_run: brevoSandbox,
                        inbox_via_brevo: e.target.checked,
                        default_sender_email: senderEmail.trim() || null,
                        default_sender_name: senderName.trim() || null,
                      })
                    }
                  />
                  Inbox sync via Brevo log
                </label>
                <Field compact label="Afzender e-mail">
                  <TextInput value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} />
                </Field>
                <Field compact label="Afzender naam">
                  <TextInput value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                </Field>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    onSaveConfig({
                      send_via_brevo: sendViaBrevo,
                      emit_brevo_events: emitBrevoEvents,
                      brevo_sandbox_dry_run: brevoSandbox,
                      inbox_via_brevo: inboxViaBrevo,
                      default_sender_email: senderEmail.trim() || null,
                      default_sender_name: senderName.trim() || null,
                    })
                  }
                >
                  Instellingen opslaan
                </Button>
                <BrevoCampaignsPanel
                  integrationId={integration.id}
                  enabled={integration.status === 'active'}
                />
              </div>
            </details>
          ) : null}

          {(integration.connector_type === 'ga4' || integration.connector_type === 'gsc') && canSync ? (
            <form
              className={styles.inlineForm}
              onSubmit={(e) => {
                e.preventDefault();
                if (integration.connector_type === 'ga4') {
                  onSaveConfig({ property_id: propertyId.trim() });
                } else {
                  onSaveConfig({ site_url: siteUrl.trim() });
                }
              }}
            >
              {integration.connector_type === 'ga4' ? (
                <Field label="GA4 property ID">
                  <TextInput value={propertyId} onChange={(ev) => setPropertyId(ev.target.value)} />
                </Field>
              ) : (
                <Field label="Site URL (Search Console)">
                  <TextInput value={siteUrl} onChange={(ev) => setSiteUrl(ev.target.value)} />
                </Field>
              )}
              <Button type="submit" variant="secondary" disabled={busy}>
                Config opslaan &amp; sync
              </Button>
            </form>
          ) : null}

          <div className={styles.toolbar}>
            <Button variant="secondary" disabled={busy} onClick={onTest}>
              Test
            </Button>
            {canSync ? (
              <Button variant="ghost" disabled={busy} onClick={onSync}>
                Sync
              </Button>
            ) : null}
            <Button variant="ghost" disabled={busy} onClick={onDelete}>
              Verwijderen
            </Button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function summaryLine(integration: IntegrationPublic): string {
  const parts: string[] = [];
  if (integration.connector_type === 'ga4' && integration.config.property_id) {
    parts.push(`Property ${integration.config.property_id}`);
  }
  if (integration.config.site_url) {
    parts.push(String(integration.config.site_url));
  }
  if (integration.last_sync_at) {
    parts.push(`Sync ${new Date(integration.last_sync_at).toLocaleDateString('nl-NL')}`);
  } else if (integration.has_credentials) {
    parts.push('Credentials OK');
  }
  return parts.join(' · ') || 'Nog niet gesynchroniseerd';
}

function factRows(integration: IntegrationPublic): [string, string][] {
  const rows: [string, string][] = [];
  const email =
    typeof integration.config.service_account_email === 'string'
      ? integration.config.service_account_email
      : null;
  if (email) rows.push(['Service account', email]);
  if (integration.connector_type === 'brevo' && integration.config.account_email) {
    rows.push(['Brevo', String(integration.config.account_email)]);
  }
  if (integration.config.wp_user) rows.push(['WP-gebruiker', String(integration.config.wp_user)]);
  if (integration.config.site_name) rows.push(['Sitenaam', String(integration.config.site_name)]);
  if (integration.last_sync_at) {
    rows.push(['Laatste sync', new Date(integration.last_sync_at).toLocaleString('nl-NL')]);
  }
  if (integration.has_credentials) rows.push(['Credentials', 'Versleuteld opgeslagen']);
  return rows;
}
